import React, { useCallback, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Modal as BootstrapModal } from 'react-bootstrap';

import { margin } from 'polished';
import { useToast } from '../../../hooks/toast';

import getResponseErrors from '../../../utils/getResponseErrors';
import getValidationErrors from '../../../utils/getValidationErrors';
import Input from '../../Input';
import TextArea from '../../TextArea';
import InputCheck from '../../InputCheck';

import { Container, FormGroup } from './styles';

import { ICreateMedicamentoDTO, IMedicamento } from '../../../hooks/receita';

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (medicamento: IMedicamento) => void;
  receitaStatus: 'Criada' | 'Emitida' | 'Cancelada';
  initialData?: IMedicamento;
  isEditing?: boolean;
}

const MedicamentoForm: React.FC<Props> = ({
  show,
  onClose,
  onSave,
  receitaStatus,
  initialData,
  isEditing = false,
}) => {
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);

  const initialFormatado = initialData
    ? {
        ...initialData,
        MedControlado: initialData.MedControlado ? 'true' : 'false',
      }
    : undefined;

  const canEdit = receitaStatus === 'Criada';

  const handleSubmit = useCallback(
    async (dados: {
      MedNome: string;
      MedQuantidade: string;
      MedObservacao: string;
      MedControlado?: boolean;
    }) => {
      if (!canEdit) {
        addToast({
          type: 'error',
          title: 'Não permitido',
          description:
            'Não é possível alterar medicamentos de uma receita que não está com status "Criada"',
        });
        return;
      }

      setLoading(true);
      try {
        formRef.current?.setErrors({});

        const dadosMedicamento: ICreateMedicamentoDTO = {
          MedNome: dados.MedNome.toUpperCase(),
          MedQuantidade: dados.MedQuantidade,
          MedObservacao: dados.MedObservacao?.toUpperCase() || '',
          MedControlado: dados.MedControlado?.toString() === 'true',
        };

        // Include MedCodigo from initialData when editing
        const medicamentoCompleto = {
          ...dadosMedicamento,
          ...(initialData?.MedCodigo && { MedCodigo: initialData.MedCodigo }),
        };

        onSave(medicamentoCompleto as IMedicamento);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        }
        const mensagem = getResponseErrors(error);
        addToast({
          title: 'Não foi possível salvar medicamento',
          type: 'error',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, canEdit, onSave, initialData],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const getSubmitButtonText = (): string => {
    if (loading) return 'Salvando...';
    if (isEditing) return 'Atualizar';
    return 'Adicionar';
  };

  if (!show) return null;

  return (
    <BootstrapModal show={show} onHide={handleClose} size="lg" centered>
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>
          {isEditing ? 'Editar Medicamento' : 'Novo Medicamento'}
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <Form
          ref={formRef}
          initialData={initialFormatado || {}}
          onSubmit={handleSubmit}
        >
          <Container>
            <FormGroup>
              <Input
                name="MedNome"
                label="Nome do Medicamento *"
                placeholder="Ex: Dipirona 1G"
                maxLength={100}
                disabled={!canEdit}
                required
              />
            </FormGroup>

            <FormGroup>
              <Input
                name="MedQuantidade"
                label="Quantidade *"
                placeholder="Ex: 1 Caixa, 30 Comprimidos"
                maxLength={100}
                disabled={!canEdit}
                required
              />
            </FormGroup>

            <FormGroup>
              <TextArea
                name="MedObservacao"
                label="Posologia"
                placeholder="Ex: Tomar 1 Comprimido via oral 3x ao dia durante 7 dias"
                maxLength={255}
                rows={3}
                disabled={!canEdit}
              />
              <span
                className="text-muted pl-2"
                style={{ wordSpacing: '12px', fontWeight: 'bold' }}
              >
                Verbo Quantidade Unidade Via Frequência Duração
              </span>
            </FormGroup>

            <FormGroup>
              <InputCheck
                name="MedControlado"
                label="Medicamento controlado (requer receita especial)"
                classSpam={{ fontWeight: 'bold' }}
                nameclass="mt-2"
                isDisabled={!canEdit}
              />
            </FormGroup>
          </Container>

          <BootstrapModal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={loading || !canEdit}
            >
              {getSubmitButtonText()}
            </Button>
          </BootstrapModal.Footer>
        </Form>
      </BootstrapModal.Body>
    </BootstrapModal>
  );
};

export default MedicamentoForm;
